import { onScopeDispose } from "vue";
import { useEventListener } from "@vueuse/core";
import { deepActiveElement, focusWithin } from "#ce/utils/focus";

// Tab wrap and dispose-time restore; sp-dialog does neither, see https://github.com/combinaut/sparkle/issues/32121.
/** Wraps mobile-map Tab at its composed-tree boundaries and restores its original opener on teardown. */
export function useFinderMapFocus(dialog) {
  const origin = deepActiveElement();
  function controls() {
    const found = [];
    const visited = new Set();
    function visit(node) {
      if (!node || visited.has(node)) {
        return;
      }
      visited.add(node);
      if (
        node instanceof HTMLElement &&
        node.matches("button, a[href], input, select, textarea, [tabindex]") &&
        node.tabIndex >= 0 &&
        !node.matches(":disabled, [inert]") &&
        node.getClientRects().length
      ) {
        found.push(node);
      }
      const children = node instanceof HTMLSlotElement ? node.assignedNodes({ flatten: true }) : node.childNodes;
      if (node.shadowRoot) {
        visit(node.shadowRoot);
      }
      for (const child of children ?? []) {
        visit(child);
      }
    }
    visit(dialog.value);
    return found;
  }
  function trap(event) {
    if (event.key !== "Tab") {
      return;
    }
    const elements = controls();
    const index = elements.indexOf(deepActiveElement());
    if (index < 0 || (event.shiftKey && index === 0) || (!event.shiftKey && index === elements.length - 1)) {
      event.preventDefault();
      (event.shiftKey ? elements.at(-1) : elements[0])?.focus();
    }
  }
  useEventListener(dialog, "keydown", trap, { capture: true });
  onScopeDispose(() => {
    const restore = focusWithin(dialog.value) || deepActiveElement() === document.body;
    if (restore) {
      queueMicrotask(() => {
        if (origin?.isConnected) {
          origin.focus({ preventScroll: true });
        }
      });
    }
  });
}
